import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { format, formatDistanceToNow } from 'date-fns';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import googleDocsIconSvg from '../../assets/icons/GoogleDocs.svg';
import { type ArtifactItem, useGetCoachMemberQuery } from './api/coachesApi';
import AdminSessionHistory from './components/AdminSessionHistory';
import { ArtifactViewDialog } from './components/ArtifactViewDialog';

export default function CoachMemberDetailPage() {
  const { coachId, memberId } = useParams<{ coachId: string; memberId: string }>();
  const navigate = useNavigate();
  const [selectedArtifact, setSelectedArtifact] = React.useState<ArtifactItem | null>(null);

  const { data, isLoading, error } = useGetCoachMemberQuery(
    { coachId: coachId!, memberId: memberId! },
    { skip: !coachId || !memberId }
  );

  const member = data?.member;
  const artifacts = data?.artifacts ?? [];
  const summaryEmails = data?.summary_emails ?? [];

  const handleArtifactClick = (artifact: ArtifactItem) => {
    setSelectedArtifact(artifact);
  };

  const handleDialogClose = () => {
    setSelectedArtifact(null);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !member) {
    return (
      <Box sx={{ maxWidth: 1200, margin: '0 auto', padding: 3 }}>
        <Alert severity="error">
          Failed to load member details. {(error as any)?.data?.error_description || ''}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/admin/coaches/${coachId}`)}
          sx={{ mt: 2 }}
        >
          Back to Coach
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', padding: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`/admin/coaches/${coachId}`)}
        sx={{ mb: 2 }}
      >
        Back to Coach
      </Button>
      <Button
        variant="contained"
        onClick={() => navigate(`/admin/coaches/${coachId}/book-series?memberId=${memberId}`)}
        sx={{ mb: 2, ml: 1 }}
      >
        Book Series
      </Button>

      {/* Member Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5">{member.name}</Typography>
        <Typography variant="body2" color="text.secondary">
          {member.email}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          {member.relationship_type && (
            <Chip
              label={`Status: ${member.relationship_type.replace(/_/g, ' ')}`}
              color={member.relationship_type === 'active' ? 'success' : 'default'}
              variant="outlined"
            />
          )}
          {member.relationship_since && (
            <Typography variant="body2" color="text.secondary">
              Member since: {format(new Date(member.relationship_since), 'MMM d, yyyy')}
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Stats Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4">{data.session_stats.session_count}</Typography>
            <Typography variant="body2" color="text.secondary">
              Sessions
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4">{artifacts.length}</Typography>
            <Typography variant="body2" color="text.secondary">
              Artifacts
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Typography variant="h6" sx={{ mb: 2 }}>
        Sessions
      </Typography>
      <AdminSessionHistory memberId={member.id} coachId={coachId} />

      {/* Artifacts Section */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Artifacts
      </Typography>
      <Paper>
        {artifacts.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No artifacts found.</Typography>
          </Box>
        ) : (
          <List>
            {artifacts.map((artifact, index) => (
              <Box key={artifact.id}>
                {index > 0 && <Divider />}
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleArtifactClick(artifact)}>
                    <ListItemIcon>
                      <img
                        src={googleDocsIconSvg}
                        alt="Google Docs"
                        style={{ width: 24, height: 24 }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={artifact.title}
                      secondary={
                        <Box component="span">
                          {artifact.transcript_title && (
                            <Typography
                              variant="caption"
                              component="span"
                              sx={{ display: 'block' }}
                            >
                              From: {artifact.transcript_title}
                            </Typography>
                          )}
                          <Typography variant="caption" component="span" sx={{ display: 'block' }}>
                            Created: {format(new Date(artifact.created_at), 'MMM d, yyyy h:mm a')}
                          </Typography>
                          <Typography variant="caption" component="span" sx={{ display: 'block' }}>
                            Updated:{' '}
                            {formatDistanceToNow(new Date(artifact.updated_at), {
                              addSuffix: true,
                            })}
                          </Typography>
                        </Box>
                      }
                    />
                    <Chip label={artifact.profile_slug} size="small" variant="outlined" />
                  </ListItemButton>
                </ListItem>
              </Box>
            ))}
          </List>
        )}
      </Paper>

      {/* Summary Emails Section */}
      <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>
        Summary Emails
      </Typography>
      <Paper sx={{ mb: 3 }}>
        {summaryEmails.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No summary emails logged yet.</Typography>
          </Box>
        ) : (
          <List>
            {summaryEmails.map((emailItem, index) => (
              <Box key={`${emailItem.sent_at}-${index}`}>
                {index > 0 && <Divider />}
                <ListItem>
                  <ListItemText
                    primary={emailItem.email_subject ?? 'Summary email'}
                    secondary={
                      <Box component="span">
                        {emailItem.compact_summary && (
                          <Typography variant="caption" component="span" sx={{ display: 'block' }}>
                            {emailItem.compact_summary}
                          </Typography>
                        )}
                        <Typography variant="caption" component="span" sx={{ display: 'block' }}>
                          Sent{' '}
                          {formatDistanceToNow(new Date(emailItem.sent_at), { addSuffix: true })}
                        </Typography>
                        {emailItem.to_email && (
                          <Typography variant="caption" component="span" sx={{ display: 'block' }}>
                            To: {emailItem.to_email}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              </Box>
            ))}
          </List>
        )}
      </Paper>

      {/* Artifact View Dialog */}
      {selectedArtifact && (
        <ArtifactViewDialog
          open={!!selectedArtifact}
          onClose={handleDialogClose}
          artifactId={selectedArtifact.id}
          artifactTitle={selectedArtifact.title}
          profileSlug={selectedArtifact.profile_slug}
          transcriptTitle={selectedArtifact.transcript_title}
        />
      )}
    </Box>
  );
}
